import auth from '@/auth/auth';
import { BadgeVariant } from '@/components/Badge/Badge.vue';
import { AllocationUsage } from '@/components/Timeline/allocation/types';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import { useTimelineStore } from '@/components/Timeline/Timeline.store';
import { PhaseTypeDatabase } from '@/components/Timeline/Timeline.types';
import {
    eventStatusToBadgeVariant,
    eventVariantStatusToBadgeVariant,
    getEventAllocationsRoute,
    getEventStatusLabel,
    getEventVariantStatusLabel,
    mapEventAllocationForTimeline,
} from '@/event/event';
import {
    ExternalEventAllocationUsageType,
    GetSectionsDataForAllocationDesignerTimelineQuery,
} from '@/generated/graphql';
import {
    getProjectAllocationsRoute,
    getProjectStatusLabel,
    getProjectVariantStatusLabel,
    mapProjectAllocationForTimeline,
    projectStatusToBadgeVariant,
    projectVariantStatusToBadgeVariant,
} from '@/project/project';
import { computed, ComputedRef, Ref, toValue } from 'vue';

// FIXME move this to a generic place, if event/project also get
// the extended Tooltip variant and need to map
function mapCompanyGroup(companyGroup: string | undefined) {
    if (companyGroup === undefined) return null;
    if (companyGroup === 'Koelnmesse GmbH') {
        return 'Koelnmesse';
    }
    if (companyGroup === 'Koelncongress GmbH') {
        return 'Koelncongress';
    }
    return companyGroup;
}

export function useDesignerTimelineData(
    result: Ref<GetSectionsDataForAllocationDesignerTimelineQuery | undefined>,
    visibleEventVariantIds: Ref<string[] | undefined>,
    visibleProjectVariantIds: Ref<string[] | undefined>,
) {
    const store = useTimelineStore();

    const site = computed(() => {
        const sites = result.value?.sites?.nodes;
        if (!sites) {
            throw new Error('Missing site');
        } else {
            return sites[0];
        }
    });

    const rooms = computed(() => result.value?.rooms?.nodes ?? []);

    const areas = computed(() => result.value?.areas?.nodes ?? []);

    const areAllocationsEditable = auth.isAdmin();

    const eventUsages = computed(
        () =>
            result.value?.allocationUsages?.nodes.map((n) => ({
                ...n,
                type: n.type,
                color: mapToPhaseColor(n.type as Exclude<PhaseTypeDatabase, ExternalEventAllocationUsageType>),
            })) ?? [],
    );
    const projectUsages = computed(
        () =>
            result.value?.projectAllocationUsages?.nodes.map((n) => ({
                ...n,
                type: n.type as PhaseTypeDatabase,
                color: mapToPhaseColor(n.type as Exclude<PhaseTypeDatabase, ExternalEventAllocationUsageType>),
            })) ?? [],
    );
    const usages: ComputedRef<AllocationUsage[]> = computed(() => [...eventUsages.value, ...projectUsages.value]);
    const externalEventUsages = computed(() => result.value?.externalEventAllocationUsages?.nodes);

    const eventExecutionPhasePosition = computed(() => eventUsages.value?.find((u) => u.isExecution)?.position ?? 4);
    const projectExecutionPhasePosition = computed(
        () => projectUsages.value?.find((u) => u.isExecution)?.position ?? 1,
    );
    const externalEventExecutionPhasePosition = computed(
        () => externalEventUsages.value?.find((u) => u.isExecution)?.position ?? 1,
    );

    const eventAllocations = computed(() => {
        const visibleIds = toValue(visibleEventVariantIds);
        let nodes = result.value?.allocationRooms?.nodes ?? [];
        if (visibleIds) {
            nodes = nodes.filter((allocation) => visibleIds.includes(allocation.variant.id));
        }
        return (
            nodes.map((allocation) => {
                return {
                    ...mapEventAllocationForTimeline(
                        {
                            ...allocation.variant,
                            referenceAllocationRoomId: allocation.variant.referenceAllocationRoomId ?? null,
                            referenceAllocationAreaId: allocation.variant.referenceAllocationAreaId ?? null,
                        },
                        allocation,
                        'Room',
                        eventExecutionPhasePosition.value,
                        areAllocationsEditable,
                    ),
                    linkTarget: getEventAllocationsRoute(allocation.variant.event.id),
                    allocationTooltip: {
                        // FIXME move this into the mapping code as soon as not only the DesignerTimeline should
                        // show the tooltip
                        companyGroup: mapCompanyGroup(allocation.variant.event.companyGroup),
                        businessDomain: allocation.variant.event.businessDomain ?? null,
                        title: allocation.variant.event.nameLong,
                        status: {
                            variant: eventStatusToBadgeVariant(allocation.variant.event.status) as BadgeVariant,
                            label: getEventStatusLabel(allocation.variant.event.status),
                        },
                        allocationVariant: {
                            index: allocation.variant.index,
                            name: allocation.variant.name,
                            status: {
                                variant: eventVariantStatusToBadgeVariant(allocation.variant.status) as BadgeVariant,
                                label: getEventVariantStatusLabel(allocation.variant.status),
                            },
                        },
                        responsibles: allocation.variant.event.responsible
                            ? allocation.variant.event.responsible.split(',')
                            : [],
                    },
                };
            }) ?? []
        );
    });

    const eventAreaAllocations = computed(() => {
        const visibleIds = toValue(visibleEventVariantIds);
        let nodes = result.value?.allocationAreas?.nodes ?? [];
        if (visibleIds) {
            nodes = nodes.filter((allocation) => visibleIds.includes(allocation.variant.id));
        }
        return (
            nodes.map((allocation) => {
                return {
                    ...mapEventAllocationForTimeline(
                        {
                            ...allocation.variant,
                            referenceAllocationRoomId: allocation.variant.referenceAllocationRoomId ?? null,
                            referenceAllocationAreaId: allocation.variant.referenceAllocationAreaId ?? null,
                        },
                        allocation,
                        'Area',
                        4,
                        areAllocationsEditable,
                    ),
                    linkTarget: getEventAllocationsRoute(allocation.variant.event.id),
                    allocationTooltip: {
                        // FIXME move this into the mapping code as soon as not only the DesignerTimeline should
                        // show the tooltip
                        companyGroup: mapCompanyGroup(allocation.variant.event.companyGroup),
                        businessDomain: allocation.variant.event.businessDomain ?? null,
                        title: allocation.variant.event.nameLong,
                        status: {
                            variant: eventStatusToBadgeVariant(allocation.variant.event.status) as BadgeVariant,
                            label: getEventStatusLabel(allocation.variant.event.status),
                        },
                        allocationVariant: {
                            index: allocation.variant.index,
                            name: allocation.variant.name,
                            status: {
                                variant: eventVariantStatusToBadgeVariant(allocation.variant.status) as BadgeVariant,
                                label: getEventVariantStatusLabel(allocation.variant.status),
                            },
                        },
                        responsibles: allocation.variant.event.responsible
                            ? allocation.variant.event.responsible.split(',')
                            : [],
                    },
                };
            }) ?? []
        );
    });

    const projectAllocations = computed(() => {
        const visibleIds = toValue(visibleProjectVariantIds);
        let nodes = result.value?.projectAllocationRooms?.nodes ?? [];
        if (visibleIds) {
            nodes = nodes.filter((allocation) => visibleIds.includes(allocation.variant.id));
        }
        return (
            nodes.map((allocation) => {
                return {
                    ...mapProjectAllocationForTimeline(
                        {
                            ...allocation.variant,
                            referenceAllocationRoomId: allocation.variant.referenceProjectAllocationRoomId ?? null,
                            referenceAllocationAreaId: allocation.variant.referenceProjectAllocationAreaId ?? null,
                        },
                        allocation,
                        'Room',
                        projectExecutionPhasePosition.value,
                        areAllocationsEditable,
                    ),
                    linkTarget: getProjectAllocationsRoute(allocation.variant.project.id),
                    allocationTooltip: {
                        // FIXME move this into the mapping code as soon as not only the DesignerTimeline should
                        // show the tooltip
                        companyGroup: mapCompanyGroup(allocation.variant.project.companyGroup),
                        businessDomain: allocation.variant.project.businessDomain ?? null,
                        title: allocation.variant.project.nameLong,
                        status: {
                            variant: projectStatusToBadgeVariant(allocation.variant.project.status) as BadgeVariant,
                            label: getProjectStatusLabel(allocation.variant.project.status),
                        },
                        allocationVariant: {
                            index: allocation.variant.index,
                            name: allocation.variant.name,
                            status: {
                                variant: projectVariantStatusToBadgeVariant(allocation.variant.status) as BadgeVariant,
                                label: getProjectVariantStatusLabel(allocation.variant.status),
                            },
                        },
                        responsibles: [],
                    },
                };
            }) ?? []
        );
    });

    const projectAreaAllocations = computed(() => {
        const visibleIds = toValue(visibleProjectVariantIds);
        let nodes = result.value?.projectAllocationAreas?.nodes ?? [];
        if (visibleIds) {
            nodes = nodes.filter((allocation) => visibleIds.includes(allocation.variant.id));
        }
        return (
            nodes.map((allocation) => {
                return {
                    ...mapProjectAllocationForTimeline(
                        {
                            ...allocation.variant,
                            referenceAllocationRoomId: allocation.variant.referenceProjectAllocationRoomId ?? null,
                            referenceAllocationAreaId: allocation.variant.referenceProjectAllocationAreaId ?? null,
                        },
                        allocation,
                        'Area',
                        projectExecutionPhasePosition.value,
                        areAllocationsEditable,
                    ),
                    linkTarget: getProjectAllocationsRoute(allocation.variant.project.id),
                    allocationTooltip: {
                        // FIXME move this into the mapping code as soon as not only the DesignerTimeline should
                        // show the tooltip
                        companyGroup: mapCompanyGroup(allocation.variant.project.companyGroup),
                        businessDomain: allocation.variant.project.businessDomain ?? null,
                        title: allocation.variant.project.nameLong,
                        status: {
                            variant: projectStatusToBadgeVariant(allocation.variant.project.status) as BadgeVariant,
                            label: getProjectStatusLabel(allocation.variant.project.status),
                        },
                        allocationVariant: {
                            index: allocation.variant.index,
                            name: allocation.variant.name,
                            status: {
                                variant: projectVariantStatusToBadgeVariant(allocation.variant.status) as BadgeVariant,
                                label: getProjectVariantStatusLabel(allocation.variant.status),
                            },
                        },
                        responsibles: [],
                    },
                };
            }) ?? []
        );
    });

    const allocations = computed(() => {
        return [
            ...eventAllocations.value,
            ...projectAllocations.value,
            ...eventAreaAllocations.value,
            ...projectAreaAllocations.value,
        ];
    });

    const countsMap = computed(() => {
        if (allocations.value) {
            const reducedMap = allocations.value.reduce((map, { locationId }) => {
                return map.set(locationId, (map.get(locationId) ?? 0) + 1);
            }, new Map<string, number>());
            if (reducedMap.entries().next().value) {
                store.setLocationAllocationTotalCounts(reducedMap);
            }
            return reducedMap;
        }
    });

    return {
        site,
        rooms,
        areas,
        usages,
        visibleEventVariantIds,
        visibleProjectVariantIds,
        allocations,
        countsMap,
        externalEventExecutionPhasePosition,
    };
}
